import { useQuery } from "@tanstack/react-query";
import { parametersApi } from "./axios";
import {
  DataI,
  MigrationsI,
  ParameterI,
  ParameterSimpleI,
  ParameterStkI,
  ParameterSystemCredentialsMailI,
  PropsI,
  PropsParametersI,
  ServiceResponseI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";
import {
  TYPE_MATERIAL_CODE,
  TYPE_IDENTIFICATION_CODE,
  METHOD_PAYMENT_CODE,
  TYPE_PAYMENT_CODE,
  TYPE_ACCOUNT_BANK_CODE,
  CONTACT_TYPES_MAIL_CODE,
  CONTACT_TYPES_PHONE_CODE,
  COLLECTION_TYPES_CODE,
  STATUS_PROJECT_CODE,
  PROFILE_TYPES_CODE,
  CLIENT_TYPES_CODE,
} from "../../constants";

const getAllParameters = async ({
  pagination,
  sorting,
  globalFilter,
  all,
  parent,
}: PropsParametersI): Promise<ServiceResponseI<DataI<ParameterI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));

    all && params.append("all", all.toString());
    parent && params.append("parent", parent.toString());

    //headers: { 'Authorization': validateToken
    const { data } = await parametersApi.get<
      ServiceResponseI<DataI<ParameterI>>
    >("", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterTypeMaterial = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", TYPE_MATERIAL_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterTypeIdentification = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", TYPE_IDENTIFICATION_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterTypePayment = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", TYPE_PAYMENT_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getSystemConfig = async () => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.get("/get-system-config", {
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const updateSystemConfig = async (payload: Record<string, any>) => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.put("/massive-name-value", payload, {
      headers: { Authorization: token },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

/**
 * Descarga el banner/logo que se incrusta en los correos.
 * El backend responde con el archivo binario ubicado en
 * `path.join(pathSaveFile(), credentials.logo)` (ruta y nombre viven en BD).
 */
export const getSystemMailLogo = async (): Promise<Blob> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.get("/system-mail-logo", {
      headers: { Authorization: token },
      responseType: "blob",
    });
    return data as Blob;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error("No se pudo obtener el banner actual");
  }
};

/**
 * Reemplaza EN DISCO el archivo del banner/logo de correos.
 * No cambia la ruta ni el nombre (eso vive en BD): el backend sobrescribe
 * el archivo existente en `path.join(pathSaveFile(), credentials.logo)`.
 * Envía multipart/form-data con el campo `file`.
 */
export const updateSystemMailLogo = async (
  file: File,
): Promise<ServiceResponseI<string>> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const form = new FormData();
    form.append("file", file);
    const { data } = await parametersApi.put<ServiceResponseI<string>>(
      "/system-mail-logo",
      form,
      { headers: { Authorization: token } }, // sin Content-Type: axios pone el boundary
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterTypeAccountBank = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", TYPE_ACCOUNT_BANK_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterMethodPayment = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", METHOD_PAYMENT_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllContactsTypeMail = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", CONTACT_TYPES_MAIL_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllContactsTypePhone = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", CONTACT_TYPES_PHONE_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllCollectionTypes = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", COLLECTION_TYPES_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllProformStatus = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", STATUS_PROJECT_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const parameterGetByCode = async (code: string) => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", code);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI>>(
      "/by-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const parameterGetNoAuthByCode = async (code: string) => {
  try {
    const params = new URLSearchParams();

    params.append("code", code);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI>>(
      "/by-code-no-auth",
      { params },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterTypeProfile = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", PROFILE_TYPES_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterTypeClient = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", CLIENT_TYPES_CODE);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-children-by-parent-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterSystemCredentialsMail = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await parametersApi.get<
      ServiceResponseI<ParameterSystemCredentialsMailI>
    >("/get-system-credentials-mail", {
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getParameterByCode = async (code: string) => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", code);

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI>>(
      "/get-by-code",
      { params, headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllRolesForStk = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await parametersApi.get<ServiceResponseI<ParameterStkI[]>>(
      "/all-roles-for-stk",
      { headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllRoles = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await parametersApi.get<
      ServiceResponseI<ParameterSimpleI[]>
    >("/all-roles", { headers: { Authorization: validateToken } });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const parameterPutMassive = async <T>(
  getForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.put<ServiceResponseI<string>>(
      "/massive",
      getForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const parameterPutMassiveNameValue = async <T>(
  getForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.put<ServiceResponseI<string>>(
      "/massive-name-value",
      getForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const parameterPutMassiveCode = async <T>(
  getForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.put<ServiceResponseI<string>>(
      "/massive-by-code",
      getForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterChildrenbyParentCode = async (code: string) => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("code", code);

    const { data } = await parametersApi.get<
      ServiceResponseI<ParameterSimpleI[]>
    >("/all-children-by-parent-code", {
      params,
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const renewWsService = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await parametersApi.get<ServiceResponseI<string>>(
      "/renew-ws",
      { headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const parameterDelete = async <T>(id: string): Promise<string> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.delete(`/${id}`, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const getAllParameterParents = async () => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await parametersApi.get<ServiceResponseI<ParameterI[]>>(
      "/all-parents",
      { headers: { Authorization: validateToken } },
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const parameterPost = async <T>(
  getForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.post<ServiceResponseI<string>>(
      "",
      getForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const parameterById = async (
  id: string,
): Promise<ServiceResponseI<ParameterI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.get<ServiceResponseI<ParameterI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const parameterPut = async <T>(
  id: string,
  updateForms: T,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await parametersApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useParameters = ({
  sorting,
  pagination,
  globalFilter,
  parent,
  all,
}: PropsParametersI) => {
  const parametersQuery = useQuery(
    ["parameters", { pagination, sorting, globalFilter, parent, all }],
    () => getAllParameters({ pagination, sorting, globalFilter, parent, all }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return {
    parametersQuery,
  };
};
